import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faPencil,
  faTrash,
  faDollarSign,
  faCube,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import SearchableAddDropdown from "../components/SearchableAddDropdown";
import InlineFieldTooltip from "../components/InlineFieldTooltip";
import {
  deriveCoatingCalculations,
  createDefaultCoatingFormData,
  buildCoatingPayload,
} from "../utils/coatingCalculations";
import RepairRulesManagement from "./RepairRulesManagement";

const SYSTEM_LIST_KEYS = {
  materialType: "material_type",
};

const normalizeHexColor = (value) => {
  const raw = String(value || "").trim();
  const shortHexMatch = raw.match(/^#([0-9a-fA-F]{3})$/);
  if (shortHexMatch) {
    const [r, g, b] = shortHexMatch[1].split("");
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  const hexMatch = raw.match(/^#([0-9a-fA-F]{6})$/);
  if (hexMatch) {
    return `#${hexMatch[1].toLowerCase()}`;
  }

  return null;
};

const getBadgeTextColorFromBase = (baseColor) => {
  const normalizedHex = normalizeHexColor(baseColor);
  if (!normalizedHex) return null;

  const r = parseInt(normalizedHex.slice(1, 3), 16);
  const g = parseInt(normalizedHex.slice(3, 5), 16);
  const b = parseInt(normalizedHex.slice(5, 7), 16);

  // Darken to create stronger text contrast over a light-tinted badge background.
  const contrastFactor = 0.42;
  const toHex = (channel) =>
    Math.max(0, Math.min(255, Math.round(channel * contrastFactor)))
      .toString(16)
      .padStart(2, "0");

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const normalizeProcessLayerKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const MaterialsMasterManagement = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();

  const getInitialTabFromSearch = useCallback((search) => {
    const params = new URLSearchParams(search || "");
    const raw = String(params.get("tab") || "")
      .trim()
      .toLowerCase();

    if (raw === "repair-rules") return "repair_rules";
    if (raw === "process-cost-mappings") return "process_cost_mappings";
    if (raw === "paint-layers") return "paint_layers";
    return "paints";
  }, []);

  const [activeTab, setActiveTab] = useState(() =>
    getInitialTabFromSearch(location.search),
  );
  const [loadingLayers, setLoadingLayers] = useState(true);
  const [loadingPaints, setLoadingPaints] = useState(true);
  const [error, setError] = useState(null);

  const [paintLayers, setPaintLayers] = useState([]);
  const [paints, setPaints] = useState([]);
  const [paintDefaults, setPaintDefaults] = useState([]);
  const [expandedLayerYears, setExpandedLayerYears] = useState(new Set());
  const [expandedPaintYears, setExpandedPaintYears] = useState(new Set());

  const [layerAvailableYears, setLayerAvailableYears] = useState([]);
  const [paintAvailableYears, setPaintAvailableYears] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [modalEntity, setModalEntity] = useState("paint");
  const [editingRecord, setEditingRecord] = useState(null);

  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyEntity, setCopyEntity] = useState("layer");
  const [copySourceYear, setCopySourceYear] = useState(null);
  const [copyTargetYear, setCopyTargetYear] = useState("");
  const [copyError, setCopyError] = useState("");
  const [copying, setCopying] = useState(false);

  const [formData, setFormData] = useState(
    createDefaultCoatingFormData(currentYear, false),
  );
  const [lastEditedUnit, setLastEditedUnit] = useState({
    cost: null,
    coverage: null,
    wft: null,
    dft: null,
  });

  const [layerSearchTerm, setLayerSearchTerm] = useState("");
  const [layerMaterialTypeFilter, setLayerMaterialTypeFilter] = useState("");
  const [layerYearFilter, setLayerYearFilter] = useState("");

  const [paintSearchTerm, setPaintSearchTerm] = useState("");
  const [paintYearFilter, setPaintYearFilter] = useState("");

  const [materialTypeValues, setMaterialTypeValues] = useState([]);
  const [coatTypeOptions, setCoatTypeOptions] = useState([]);
  const [layerSortValues, setLayerSortValues] = useState([]);
  const [loadingProcessMappings, setLoadingProcessMappings] = useState(true);
  const [processCostMappings, setProcessCostMappings] = useState([]);
  const [mappingProcessTypes, setMappingProcessTypes] = useState([]);
  const [mappingSearchTerm, setMappingSearchTerm] = useState("");
  const [pendingMappingToggleKeys, setPendingMappingToggleKeys] = useState(
    () => new Set(),
  );
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [mappingFormData, setMappingFormData] = useState({
    repair_process_type_id: "",
    material_type: "",
    include_washing_cost: false,
    include_surface_prep_cost: false,
    counts_as_paint_layer: false,
    is_active: true,
    notes: "",
  });

  const derivedValues = deriveCoatingCalculations(formData, lastEditedUnit);

  const isLayerModal = modalEntity === "layer";

  const getMaterialIsActive = (item) =>
    item.isActive !== undefined ? item.isActive : item.is_active !== false;

  const sortBySortOrderAndName = (items = [], nameKey = "materialName") =>
    [...items].sort((a, b) => {
      const aSort = Number(a.sortOrder ?? a.sort_order ?? 99);
      const bSort = Number(b.sortOrder ?? b.sort_order ?? 99);
      if (aSort !== bSort) return aSort - bSort;

      const aName = String(a[nameKey] || a.material_name || a.paint_name || "");
      const bName = String(b[nameKey] || b.material_name || b.paint_name || "");
      return aName.localeCompare(bName);
    });

  const resetForm = useCallback(
    (entity = "paint") => {
      setLastEditedUnit({ cost: null, coverage: null, wft: null, dft: null });
      setEditingRecord(null);
      setModalEntity(entity);
      setFormData(
        createDefaultCoatingFormData(currentYear, entity === "layer"),
      );
    },
    [currentYear],
  );

  const fetchPaintLayers = useCallback(async () => {
    try {
      setLoadingLayers(true);
      const params = new URLSearchParams({
        grouped_by_year: "true",
        ...(layerSearchTerm && { search: layerSearchTerm }),
        ...(layerMaterialTypeFilter && {
          material_type: layerMaterialTypeFilter,
        }),
        ...(layerYearFilter && { year: layerYearFilter }),
      });

      const response = await api.get(`/materials-master?${params}`);
      if (response.data.success) {
        const data = response.data.data || [];
        setPaintLayers(data);
        setExpandedLayerYears((prev) => {
          if (data.length > 0 && prev.size === 0) {
            return new Set([data[0].year]);
          }
          return prev;
        });
      }
    } catch (err) {
      setError("Failed to fetch paint layers");
      console.error("Error fetching paint layers:", err);
    } finally {
      setLoadingLayers(false);
    }
  }, [layerSearchTerm, layerMaterialTypeFilter, layerYearFilter]);

  const fetchLayerYears = useCallback(async () => {
    try {
      const response = await api.get("/materials-master?grouped_by_year=true");
      if (response.data.success) {
        const years = Array.from(
          new Set(
            (response.data.data || [])
              .map((g) => Number(g.year))
              .filter((y) => Number.isFinite(y)),
          ),
        ).sort((a, b) => b - a);
        setLayerAvailableYears(years);
      }
    } catch (err) {
      console.error("Error fetching layer years:", err);
    }
  }, []);

  const fetchPaints = useCallback(async () => {
    try {
      setLoadingPaints(true);
      const params = new URLSearchParams({
        limit: "1000",
        ...(paintSearchTerm && { search: paintSearchTerm }),
        ...(paintYearFilter && { year: paintYearFilter }),
      });

      const response = await api.get(`/paints-master?${params}`);
      if (response.data.success) {
        const list = response.data.data || [];
        setPaints(sortBySortOrderAndName(list, "paintName"));
        setExpandedPaintYears((prev) => {
          if (list.length > 0 && prev.size === 0) {
            const topYear = list
              .map((item) => Number(item.year))
              .filter((year) => Number.isFinite(year))
              .sort((a, b) => b - a)[0];
            return Number.isFinite(topYear) ? new Set([topYear]) : prev;
          }
          return prev;
        });
        const years = Array.from(
          new Set(
            list
              .map((item) => Number(item.year))
              .filter((year) => Number.isFinite(year)),
          ),
        ).sort((a, b) => b - a);
        setPaintAvailableYears(years);
      }
    } catch (err) {
      setError("Failed to fetch paints");
      console.error("Error fetching paints:", err);
    } finally {
      setLoadingPaints(false);
    }
  }, [paintSearchTerm, paintYearFilter]);

  const fetchPaintDefaults = useCallback(async () => {
    try {
      const response = await api.get("/paints-master?limit=1000");
      if (response.data.success) {
        setPaintDefaults(response.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching paint defaults:", err);
      setPaintDefaults([]);
    }
  }, []);

  const fetchCoatTypeOptions = useCallback(async () => {
    try {
      const response = await api.get("/system-lists", {
        params: { list: "Coat Type", limit: 500 },
      });
      const rows = response.data?.data || [];
      setCoatTypeOptions(
        rows.map((row) => String(row.value || "").trim()).filter(Boolean),
      );
    } catch (err) {
      console.error("Error fetching Coat Type system list:", err);
      setCoatTypeOptions([]);
    }
  }, []);

  const fetchMaterialTypeOptions = useCallback(async () => {
    try {
      const response = await api.get("/system-lists", {
        params: {
          list_key: SYSTEM_LIST_KEYS.materialType,
          limit: 500,
        },
      });
      const rows = response.data?.data || [];
      setMaterialTypeValues(rows);
    } catch (err) {
      console.error("Error fetching Material Type system list:", err);
      setMaterialTypeValues([]);
    }
  }, []);

  const fetchLayerSortValues = useCallback(async () => {
    try {
      const response = await api.get("/system-lists", {
        params: { list: "Layers", limit: 500 },
      });
      const rows = response.data?.data || [];
      setLayerSortValues(rows);
    } catch (err) {
      console.error("Error fetching Layers system list:", err);
      setLayerSortValues([]);
    }
  }, []);

  const fetchMappingProcessTypes = useCallback(async () => {
    try {
      const response = await api.get("/repair-process-types");
      if (response.data?.success) {
        setMappingProcessTypes(response.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching repair process types for mappings:", err);
      setMappingProcessTypes([]);
    }
  }, []);

  const fetchProcessCostMappings = useCallback(async () => {
    try {
      setLoadingProcessMappings(true);
      const params = new URLSearchParams({
        ...(mappingSearchTerm && { search: mappingSearchTerm }),
      });

      const response = await api.get(`/repair-process-cost-mappings?${params}`);
      if (response.data?.success) {
        setProcessCostMappings(response.data.data || []);
      }
    } catch (err) {
      setError("Failed to fetch process cost mappings");
      console.error("Error fetching process cost mappings:", err);
    } finally {
      setLoadingProcessMappings(false);
    }
  }, [mappingSearchTerm]);

  useEffect(() => {
    fetchPaintLayers();
    fetchLayerYears();
  }, [fetchPaintLayers, fetchLayerYears]);

  useEffect(() => {
    fetchPaints();
  }, [fetchPaints]);

  useEffect(() => {
    fetchPaintDefaults();
  }, [fetchPaintDefaults]);

  useEffect(() => {
    fetchCoatTypeOptions();
    fetchMaterialTypeOptions();
    fetchLayerSortValues();
  }, [fetchCoatTypeOptions, fetchMaterialTypeOptions, fetchLayerSortValues]);

  useEffect(() => {
    fetchMappingProcessTypes();
  }, [fetchMappingProcessTypes]);

  useEffect(() => {
    fetchProcessCostMappings();
  }, [fetchProcessCostMappings]);

  useEffect(() => {
    setActiveTab(getInitialTabFromSearch(location.search));
  }, [location.search, getInitialTabFromSearch]);

  const handleTabChange = useCallback(
    (nextTab) => {
      setActiveTab(nextTab);

      const params = new URLSearchParams(location.search || "");
      if (nextTab === "paints") {
        params.delete("tab");
      } else if (nextTab === "paint_layers") {
        params.set("tab", "paint-layers");
      } else if (nextTab === "repair_rules") {
        params.set("tab", "repair-rules");
      } else if (nextTab === "process_cost_mappings") {
        params.set("tab", "process-cost-mappings");
      }

      const query = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: query ? `?${query}` : "",
        },
        { replace: true },
      );
    },
    [location.pathname, location.search, navigate],
  );

  const mergedMaterialTypeOptions = useMemo(() => {
    const seen = new Set();
    return (materialTypeValues || [])
      .map((row) => String(row.value || "").trim())
      .filter(Boolean)
      .filter((value) => {
        const key = value.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [materialTypeValues]);

  const materialTypeColorMap = useMemo(() => {
    return (materialTypeValues || []).reduce((acc, row) => {
      const key = String(row.value || "")
        .trim()
        .toLowerCase();
      if (!key) return acc;

      const rawProperties = row?.properties;
      const properties =
        typeof rawProperties === "string"
          ? (() => {
              try {
                return JSON.parse(rawProperties);
              } catch {
                return {};
              }
            })()
          : rawProperties && typeof rawProperties === "object"
            ? rawProperties
            : {};

      const colorEntry = Object.entries(properties).find(
        ([propKey]) =>
          String(propKey || "")
            .trim()
            .toLowerCase() === "color",
      );
      const colorValue = colorEntry?.[1];

      if (typeof colorValue === "string" && colorValue.trim()) {
        acc[key] = colorValue.trim();
      }
      return acc;
    }, {});
  }, [materialTypeValues]);

  const sortedProcessCostMappings = useMemo(() => {
    const orderedLayerValues = [...(layerSortValues || [])].sort((left, right) => {
      const leftSortOrder = Number(left.sortOrder ?? left.sort_order ?? 100);
      const rightSortOrder = Number(right.sortOrder ?? right.sort_order ?? 100);
      if (leftSortOrder !== rightSortOrder) {
        return leftSortOrder - rightSortOrder;
      }

      return String(left.value || "").localeCompare(String(right.value || ""));
    });

    const layerSortMap = new Map(
      orderedLayerValues.map((row, index) => [
        normalizeProcessLayerKey(row.value),
        {
          sortOrder: Number(row.sortOrder ?? row.sort_order ?? 100),
          index,
        },
      ]),
    );

    return (processCostMappings || [])
      .map((mapping, index) => {
        const materialTypeKey = normalizeProcessLayerKey(
          mapping.material_type ?? mapping.materialType,
        );
        const layerEntry = layerSortMap.get(materialTypeKey);

        return {
          mapping,
          originalIndex: index,
          isLayerProcess: Boolean(layerEntry),
          layerSortOrder: layerEntry?.sortOrder ?? Number.POSITIVE_INFINITY,
          layerIndex: layerEntry?.index ?? Number.POSITIVE_INFINITY,
        };
      })
      .sort((left, right) => {
        if (left.isLayerProcess !== right.isLayerProcess) {
          return left.isLayerProcess ? -1 : 1;
        }

        if (left.isLayerProcess && right.isLayerProcess) {
          if (left.layerSortOrder !== right.layerSortOrder) {
            return left.layerSortOrder - right.layerSortOrder;
          }
          if (left.layerIndex !== right.layerIndex) {
            return left.layerIndex - right.layerIndex;
          }
        }

        return left.originalIndex - right.originalIndex;
      })
      .map((entry) => entry.mapping);
  }, [layerSortValues, processCostMappings]);

  const mergedCoatTypeOptions = useMemo(() => {
    const seen = new Set();
    return (coatTypeOptions || [])
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .filter((value) => {
        const key = value.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [coatTypeOptions]);

  const paintNameOptions = useMemo(() => {
    const targetYear = Number(formData.year);
    const allPaints = paintDefaults || [];

    const sameYearPaints = allPaints.filter(
      (paint) => Number(paint.year) === targetYear,
    );

    let source = sameYearPaints;
    let isFallbackPreviousYear = false;

    if (source.length === 0) {
      const previousYears = Array.from(
        new Set(
          allPaints
            .map((paint) => Number(paint.year))
            .filter((year) => Number.isFinite(year) && year < targetYear),
        ),
      ).sort((a, b) => b - a);

      if (previousYears.length > 0) {
        const latestPreviousYear = previousYears[0];
        source = allPaints.filter(
          (paint) => Number(paint.year) === latestPreviousYear,
        );
        isFallbackPreviousYear = true;
      }
    }

    return sortBySortOrderAndName(source, "paintName").map((paint) => ({
      value: paint.id,
      label: !isFallbackPreviousYear
        ? String(paint.paintName || paint.paint_name || "")
        : `${String(paint.paintName || paint.paint_name || "")} (${paint.year})`,
    }));
  }, [paintDefaults, formData.year]);

  const groupedPaints = useMemo(() => {
    const groups = (paints || []).reduce((acc, paint) => {
      const year = Number(paint.year);
      if (!Number.isFinite(year)) return acc;

      if (!acc[year]) {
        acc[year] = {
          year,
          currency: paint.currency || "CAD",
          paints: [],
        };
      }
      acc[year].paints.push(paint);
      return acc;
    }, {});

    return Object.values(groups)
      .map((group) => ({
        ...group,
        paints: sortBySortOrderAndName(group.paints, "paintName"),
      }))
      .sort((a, b) => b.year - a.year);
  }, [paints]);

  const handleLayerPaintSelection = useCallback(
    (paintIdValue) => {
      const selectedPaint = (paintDefaults || []).find(
        (paint) => String(paint.id) === String(paintIdValue),
      );

      if (!selectedPaint) {
        setFormData((prev) => ({
          ...prev,
          paint_id: paintIdValue || "",
        }));
        return;
      }

      setFormData((prev) => ({
        ...prev,
        paint_id: selectedPaint.id,
        material_name:
          selectedPaint.paintName || selectedPaint.paint_name || "",
        coat_type: selectedPaint.coatType || selectedPaint.coat_type || "",
        input_uom_mode:
          selectedPaint.inputUomMode ||
          selectedPaint.input_uom_mode ||
          "imperial",
        cost_per_unit_cad:
          selectedPaint.costPerUnitCad ?? selectedPaint.cost_per_unit_cad ?? 0,
        cost_per_gal_cdn:
          selectedPaint.costPerGalCdn ?? selectedPaint.cost_per_gal_cdn ?? 0,
        cost_per_litre:
          selectedPaint.costPerLitre ?? selectedPaint.cost_per_litre ?? 0,
        thickness_mils:
          selectedPaint.thicknessMils ?? selectedPaint.thickness_mils ?? 0,
        wft_microns: selectedPaint.wftMicrons ?? selectedPaint.wft_microns ?? 0,
        dft_mils: selectedPaint.dftMils ?? selectedPaint.dft_mils ?? 0,
        dft_microns: selectedPaint.dftMicrons ?? selectedPaint.dft_microns ?? 0,
        coverage_ft2_per_gal:
          selectedPaint.coverageFt2PerGal ??
          selectedPaint.coverage_ft2_per_gal ??
          0,
        coverage_25um_m2_per_l:
          selectedPaint.coverage25umM2PerL ??
          selectedPaint.coverage_25um_m2_per_l ??
          0,
        coverage_field_mils:
          selectedPaint.coverageFieldMils ??
          selectedPaint.coverage_field_mils ??
          0,
        cost_per_unit_cdn:
          selectedPaint.costPerUnitCdn ?? selectedPaint.cost_per_unit_cdn ?? 0,
        cost_per_m2_cdn:
          selectedPaint.costPerM2Cdn ?? selectedPaint.cost_per_m2_cdn ?? 0,
        currency: selectedPaint.currency || prev.currency || "CAD",
        sort_order: selectedPaint.sortOrder ?? selectedPaint.sort_order ?? 99,
      }));

      setLastEditedUnit({ cost: null, coverage: null, wft: null, dft: null });
    },
    [paintDefaults],
  );

  const handleAddCoatTypeOption = async (newValue) => {
    try {
      await api.post("/system-lists", {
        list: "Coat Type",
        value: newValue,
        is_active: true,
      });
      await fetchCoatTypeOptions();
      return true;
    } catch (err) {
      alert(err.response?.data?.message || "Failed to add Coat Type option.");
      return false;
    }
  };

  const handleAddMaterialTypeOption = async (newValue) => {
    try {
      await api.post("/system-lists", {
        list_key: SYSTEM_LIST_KEYS.materialType,
        list: "Layers",
        value: newValue,
        is_active: true,
      });
      await fetchMaterialTypeOptions();
      return true;
    } catch (err) {
      alert(
        err.response?.data?.message || "Failed to add Material Type option.",
      );
      return false;
    }
  };

  const openPaintModal = (paint = null) => {
    setModalEntity("paint");
    setLastEditedUnit({ cost: null, coverage: null, wft: null, dft: null });

    if (!paint) {
      setEditingRecord(null);
      setFormData(createDefaultCoatingFormData(currentYear, false));
      setShowModal(true);
      return;
    }

    setEditingRecord(paint);
    setFormData({
      coat_type: paint.coatType || paint.coat_type || "",
      material_name: paint.paintName || paint.paint_name || "",
      input_uom_mode: paint.inputUomMode || paint.input_uom_mode || "imperial",
      cost_per_unit_cad: paint.costPerUnitCad || paint.cost_per_unit_cad || "",
      cost_per_gal_cdn: paint.costPerGalCdn || paint.cost_per_gal_cdn || "",
      cost_per_litre: paint.costPerLitre || paint.cost_per_litre || "",
      thickness_mils: paint.thicknessMils || paint.thickness_mils || "",
      wft_microns: paint.wftMicrons || paint.wft_microns || "",
      dft_mils: paint.dftMils || paint.dft_mils || "",
      dft_microns: paint.dftMicrons || paint.dft_microns || "",
      coverage_ft2_per_gal:
        paint.coverageFt2PerGal || paint.coverage_ft2_per_gal || "",
      coverage_25um_m2_per_l:
        paint.coverage25umM2PerL || paint.coverage_25um_m2_per_l || "",
      coverage_field_mils:
        paint.coverageFieldMils || paint.coverage_field_mils || "",
      cost_per_unit_cdn: paint.costPerUnitCdn || paint.cost_per_unit_cdn || "",
      cost_per_m2_cdn: paint.costPerM2Cdn || paint.cost_per_m2_cdn || "",
      year: paint.year,
      currency: paint.currency || "CAD",
      sort_order: paint.sortOrder ?? paint.sort_order ?? 99,
      is_active: getMaterialIsActive(paint),
    });
    setShowModal(true);
  };

  const openLayerModal = (material = null) => {
    setModalEntity("layer");
    setLastEditedUnit({ cost: null, coverage: null, wft: null, dft: null });

    if (!material) {
      setEditingRecord(null);
      setFormData(createDefaultCoatingFormData(currentYear, true));
      setShowModal(true);
      return;
    }

    const existingPaintName =
      material.materialName || material.material_name || "";
    const fallbackPaint = (paintDefaults || []).find(
      (paint) =>
        Number(paint.year) === Number(material.year) &&
        String(paint.paintName || paint.paint_name || "")
          .trim()
          .toLowerCase() === String(existingPaintName).trim().toLowerCase(),
    );

    setEditingRecord(material);
    setFormData({
      material_type: material.materialType || material.material_type || "",
      paint_id:
        material.paintId || material.paint_id || fallbackPaint?.id || "",
      coat_type: material.coatType || material.coat_type || "",
      material_name: existingPaintName,
      input_uom_mode:
        material.inputUomMode || material.input_uom_mode || "imperial",
      cost_per_unit_cad:
        material.costPerUnitCad || material.cost_per_unit_cad || "",
      cost_per_gal_cdn:
        material.costPerGalCdn || material.cost_per_gal_cdn || "",
      cost_per_litre: material.costPerLitre || material.cost_per_litre || "",
      thickness_mils: material.thicknessMils || material.thickness_mils || "",
      wft_microns: material.wftMicrons || material.wft_microns || "",
      dft_mils: material.dftMils || material.dft_mils || "",
      dft_microns: material.dftMicrons || material.dft_microns || "",
      coverage_ft2_per_gal:
        material.coverageFt2PerGal || material.coverage_ft2_per_gal || "",
      coverage_25um_m2_per_l:
        material.coverage25umM2PerL || material.coverage_25um_m2_per_l || "",
      coverage_field_mils:
        material.coverageFieldMils || material.coverage_field_mils || "",
      cost_per_unit_cdn:
        material.costPerUnitCdn || material.cost_per_unit_cdn || "",
      cost_per_m2_cdn: material.costPerM2Cdn || material.cost_per_m2_cdn || "",
      year: material.year,
      currency: material.currency || "CAD",
      sort_order: material.sortOrder ?? material.sort_order ?? 99,
      is_active: getMaterialIsActive(material),
    });

    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!String(formData.material_name || "").trim()) {
      setError(isLayerModal ? "Paint is required" : "Paint Name is required");
      return;
    }

    if (isLayerModal && !String(formData.material_type || "").trim()) {
      setError("Layer Name is required");
      return;
    }

    if (isLayerModal && !String(formData.paint_id || "").trim()) {
      setError("Paint is required");
      return;
    }

    try {
      const payload = buildCoatingPayload(formData, derivedValues);

      if (isLayerModal) {
        if (editingRecord) {
          await api.put(`/materials-master/${editingRecord.id}`, payload);
        } else {
          await api.post("/materials-master", payload);
        }

        await fetchPaintLayers();
        await fetchLayerYears();
      } else {
        const { material_type, material_name, ...rest } = payload;
        const paintPayload = {
          ...rest,
          paint_name: material_name,
        };

        if (editingRecord) {
          await api.put(`/paints-master/${editingRecord.id}`, paintPayload);
        } else {
          await api.post("/paints-master", paintPayload);
        }

        await fetchPaints();
        await fetchPaintDefaults();
      }

      setShowModal(false);
      resetForm(modalEntity);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          `Failed to save ${isLayerModal ? "paint layer" : "paint"}`,
      );
      console.error("Error saving record:", err);
    }
  };

  const handleDeletePaint = async (id) => {
    if (!window.confirm("Are you sure you want to delete this paint?")) return;
    try {
      await api.delete(`/paints-master/${id}`);
      await fetchPaints();
      await fetchPaintDefaults();
    } catch (err) {
      setError("Failed to delete paint");
    }
  };

  const handleDeleteLayer = async (id) => {
    if (!window.confirm("Are you sure you want to delete this paint layer?"))
      return;
    try {
      await api.delete(`/materials-master/${id}`);
      await fetchPaintLayers();
      await fetchLayerYears();
    } catch (err) {
      setError("Failed to delete paint layer");
    }
  };

  const handleTogglePaintActive = async (paint) => {
    try {
      await api.put(`/paints-master/${paint.id}`, {
        is_active: !getMaterialIsActive(paint),
      });
      await fetchPaints();
      await fetchPaintDefaults();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update paint status");
    }
  };

  const handleToggleLayerActive = async (material) => {
    try {
      await api.put(`/materials-master/${material.id}`, {
        is_active: !getMaterialIsActive(material),
      });
      await fetchPaintLayers();
      await fetchLayerYears();
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to update paint layer status",
      );
    }
  };

  const toggleLayerYearExpansion = (year) => {
    setExpandedLayerYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  };

  const togglePaintYearExpansion = (year) => {
    setExpandedPaintYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  };

  const handleImportPreviousYear = async (targetYear) => {
    const previousYearData = paintLayers
      .filter((y) => y.year < targetYear)
      .sort((a, b) => b.year - a.year)[0];

    if (!previousYearData) {
      alert("No previous year data found to import.");
      return;
    }

    if (
      !window.confirm(
        `Import all paint layers from ${previousYearData.year} to ${targetYear}?`,
      )
    ) {
      return;
    }

    try {
      const importPromises = (previousYearData.materials || []).map(
        (material) =>
          api.post("/materials-master", {
            material_type: material.materialType || material.material_type,
            paint_id: material.paintId || material.paint_id || null,
            coat_type: material.coatType || material.coat_type || "",
            material_name: material.materialName || material.material_name,
            input_uom_mode:
              material.inputUomMode || material.input_uom_mode || "imperial",
            cost_per_unit_cad: Number(
              material.costPerUnitCad ?? material.cost_per_unit_cad ?? 0,
            ),
            cost_per_gal_cdn: Number(
              material.costPerGalCdn ?? material.cost_per_gal_cdn ?? 0,
            ),
            cost_per_litre: Number(
              material.costPerLitre ?? material.cost_per_litre ?? 0,
            ),
            thickness_mils: Number(
              material.thicknessMils ?? material.thickness_mils ?? 0,
            ),
            wft_microns: Number(
              material.wftMicrons ?? material.wft_microns ?? 0,
            ),
            dft_mils: Number(material.dftMils ?? material.dft_mils ?? 0),
            dft_microns: Number(
              material.dftMicrons ?? material.dft_microns ?? 0,
            ),
            coverage_ft2_per_gal: Number(
              material.coverageFt2PerGal ?? material.coverage_ft2_per_gal ?? 0,
            ),
            coverage_25um_m2_per_l: Number(
              material.coverage25umM2PerL ??
                material.coverage_25um_m2_per_l ??
                0,
            ),
            coverage_field_mils: Number(
              material.coverageFieldMils ?? material.coverage_field_mils ?? 0,
            ),
            cost_per_unit_cdn: Number(
              material.costPerUnitCdn ?? material.cost_per_unit_cdn ?? 0,
            ),
            cost_per_m2_cdn: Number(
              material.costPerM2Cdn ?? material.cost_per_m2_cdn ?? 0,
            ),
            sort_order: Number(material.sortOrder ?? material.sort_order ?? 99),
            year: targetYear,
            currency: material.currency || "CAD",
            is_active:
              material.isActive !== undefined
                ? material.isActive
                : material.is_active !== false,
          }),
      );

      const results = await Promise.allSettled(importPromises);
      const successful = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - successful;

      if (failed > 0) {
        alert(
          `Imported ${successful} of ${results.length} paint layers. ${failed} failed.`,
        );
      } else {
        alert(
          `Successfully imported ${successful} paint layers from ${previousYearData.year} to ${targetYear}.`,
        );
      }

      await fetchPaintLayers();
      await fetchLayerYears();
    } catch (err) {
      alert(
        "Failed to import previous year paint layers: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const handleCopyFromYear = (sourceYear) => {
    const sourceYearData = paintLayers.find((y) => y.year === sourceYear);
    if (!sourceYearData || !(sourceYearData.materials || []).length) {
      alert(`No paint layers found in source year ${sourceYear}.`);
      return;
    }

    const usedYears = new Set(layerAvailableYears.map((y) => Number(y)));
    let suggestedYear = sourceYear + 1;
    while (usedYears.has(suggestedYear) && suggestedYear <= 3000) {
      suggestedYear += 1;
    }

    setCopyEntity("layer");
    setCopySourceYear(sourceYear);
    setCopyTargetYear(String(suggestedYear));
    setCopyError("");
    setShowCopyModal(true);
  };

  const handleCopyPaintFromYear = (sourceYear) => {
    const sourceYearData = groupedPaints.find((y) => y.year === sourceYear);
    if (!sourceYearData || !(sourceYearData.paints || []).length) {
      alert(`No paints found in source year ${sourceYear}.`);
      return;
    }

    const usedYears = new Set(paintAvailableYears.map((y) => Number(y)));
    let suggestedYear = sourceYear + 1;
    while (usedYears.has(suggestedYear) && suggestedYear <= 3000) {
      suggestedYear += 1;
    }

    setCopyEntity("paint");
    setCopySourceYear(sourceYear);
    setCopyTargetYear(String(suggestedYear));
    setCopyError("");
    setShowCopyModal(true);
  };

  const handleDeletePaintYear = async (year) => {
    const yearData = groupedPaints.find((y) => y.year === year);
    const paints = (yearData && yearData.paints) || [];
    if (!paints.length) {
      alert(`No paints found for ${year}.`);
      return;
    }
    if (
      !window.confirm(
        `Delete all ${paints.length} paint(s) for ${year}? This cannot be undone.`,
      )
    )
      return;
    try {
      await Promise.all(
        paints.map((paint) => api.delete(`/paints-master/${paint.id}`)),
      );
      await fetchPaints();
      await fetchPaintDefaults();
    } catch (err) {
      setError("Failed to delete paints for year");
    }
  };

  const handleDeleteLayerYear = async (year) => {
    const yearData = paintLayers.find((y) => y.year === year);
    const materials = (yearData && yearData.materials) || [];
    if (!materials.length) {
      alert(`No paint layers found for ${year}.`);
      return;
    }
    if (
      !window.confirm(
        `Delete all ${materials.length} paint layer(s) for ${year}? This cannot be undone.`,
      )
    )
      return;
    try {
      await Promise.all(
        materials.map((material) =>
          api.delete(`/materials-master/${material.id}`),
        ),
      );
      await fetchPaintLayers();
      await fetchLayerYears();
    } catch (err) {
      setError("Failed to delete paint layers for year");
    }
  };

  const handleConfirmCopy = async () => {
    if (!copySourceYear) return;

    const targetYear = parseInt(copyTargetYear, 10);
    if (
      !Number.isInteger(targetYear) ||
      targetYear < 1900 ||
      targetYear > 3000
    ) {
      setCopyError("Please enter a valid target year.");
      return;
    }
    if (targetYear === copySourceYear) {
      setCopyError("Target year must be different from source year.");
      return;
    }
    const usedYears =
      copyEntity === "paint" ? paintAvailableYears : layerAvailableYears;

    if (usedYears.includes(targetYear)) {
      setCopyError(
        `Year ${targetYear} already exists. Please choose a new year.`,
      );
      return;
    }

    try {
      setCopying(true);
      setCopyError("");
      let copyPromises = [];

      if (copyEntity === "paint") {
        const sourceYearData = groupedPaints.find(
          (y) => y.year === copySourceYear,
        );
        if (!sourceYearData || !(sourceYearData.paints || []).length) {
          setCopyError(`No paints found in source year ${copySourceYear}.`);
          setCopying(false);
          return;
        }

        copyPromises = (sourceYearData.paints || []).map((paint) =>
          api.post("/paints-master", {
            paint_name: paint.paintName || paint.paint_name,
            coat_type: paint.coatType || paint.coat_type || "",
            input_uom_mode:
              paint.inputUomMode || paint.input_uom_mode || "imperial",
            cost_per_unit_cad: Number(
              paint.costPerUnitCad ?? paint.cost_per_unit_cad ?? 0,
            ),
            cost_per_gal_cdn: Number(
              paint.costPerGalCdn ?? paint.cost_per_gal_cdn ?? 0,
            ),
            cost_per_litre: Number(
              paint.costPerLitre ?? paint.cost_per_litre ?? 0,
            ),
            thickness_mils: Number(
              paint.thicknessMils ?? paint.thickness_mils ?? 0,
            ),
            wft_microns: Number(paint.wftMicrons ?? paint.wft_microns ?? 0),
            dft_mils: Number(paint.dftMils ?? paint.dft_mils ?? 0),
            dft_microns: Number(paint.dftMicrons ?? paint.dft_microns ?? 0),
            coverage_ft2_per_gal: Number(
              paint.coverageFt2PerGal ?? paint.coverage_ft2_per_gal ?? 0,
            ),
            coverage_25um_m2_per_l: Number(
              paint.coverage25umM2PerL ?? paint.coverage_25um_m2_per_l ?? 0,
            ),
            coverage_field_mils: Number(
              paint.coverageFieldMils ?? paint.coverage_field_mils ?? 0,
            ),
            cost_per_unit_cdn: Number(
              paint.costPerUnitCdn ?? paint.cost_per_unit_cdn ?? 0,
            ),
            cost_per_m2_cdn: Number(
              paint.costPerM2Cdn ?? paint.cost_per_m2_cdn ?? 0,
            ),
            sort_order: Number(paint.sortOrder ?? paint.sort_order ?? 99),
            year: targetYear,
            currency: paint.currency || "CAD",
            is_active:
              paint.isActive !== undefined
                ? paint.isActive
                : paint.is_active !== false,
          }),
        );
      } else {
        const sourceYearData = paintLayers.find(
          (y) => y.year === copySourceYear,
        );
        if (!sourceYearData || !(sourceYearData.materials || []).length) {
          setCopyError(
            `No paint layers found in source year ${copySourceYear}.`,
          );
          setCopying(false);
          return;
        }

        copyPromises = (sourceYearData.materials || []).map((material) =>
          api.post("/materials-master", {
            material_type: material.materialType || material.material_type,
            paint_id: material.paintId || material.paint_id || null,
            coat_type: material.coatType || material.coat_type || "",
            material_name: material.materialName || material.material_name,
            input_uom_mode:
              material.inputUomMode || material.input_uom_mode || "imperial",
            cost_per_unit_cad: Number(
              material.costPerUnitCad ?? material.cost_per_unit_cad ?? 0,
            ),
            cost_per_gal_cdn: Number(
              material.costPerGalCdn ?? material.cost_per_gal_cdn ?? 0,
            ),
            cost_per_litre: Number(
              material.costPerLitre ?? material.cost_per_litre ?? 0,
            ),
            thickness_mils: Number(
              material.thicknessMils ?? material.thickness_mils ?? 0,
            ),
            wft_microns: Number(
              material.wftMicrons ?? material.wft_microns ?? 0,
            ),
            dft_mils: Number(material.dftMils ?? material.dft_mils ?? 0),
            dft_microns: Number(
              material.dftMicrons ?? material.dft_microns ?? 0,
            ),
            coverage_ft2_per_gal: Number(
              material.coverageFt2PerGal ?? material.coverage_ft2_per_gal ?? 0,
            ),
            coverage_25um_m2_per_l: Number(
              material.coverage25umM2PerL ??
                material.coverage_25um_m2_per_l ??
                0,
            ),
            coverage_field_mils: Number(
              material.coverageFieldMils ?? material.coverage_field_mils ?? 0,
            ),
            cost_per_unit_cdn: Number(
              material.costPerUnitCdn ?? material.cost_per_unit_cdn ?? 0,
            ),
            cost_per_m2_cdn: Number(
              material.costPerM2Cdn ?? material.cost_per_m2_cdn ?? 0,
            ),
            sort_order: Number(material.sortOrder ?? material.sort_order ?? 99),
            year: targetYear,
            currency: material.currency || "CAD",
            is_active:
              material.isActive !== undefined
                ? material.isActive
                : material.is_active !== false,
          }),
        );
      }

      const results = await Promise.allSettled(copyPromises);
      const successful = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - successful;

      if (failed > 0) {
        alert(
          `Copied ${successful} of ${results.length} ${copyEntity === "paint" ? "paints" : "paint layers"}. ${failed} failed.`,
        );
      } else {
        alert(
          `Successfully copied ${successful} ${copyEntity === "paint" ? "paints" : "paint layers"} from ${copySourceYear} to ${targetYear}.`,
        );
      }

      if (copyEntity === "paint") {
        await fetchPaints();
        await fetchPaintDefaults();
      } else {
        await fetchPaintLayers();
        await fetchLayerYears();
      }

      setShowCopyModal(false);
      setCopyEntity("layer");
      setCopySourceYear(null);
      setCopyTargetYear("");
    } catch (err) {
      setCopyError(
        "Failed to copy paint layers: " +
          (err.response?.data?.message || err.message),
      );
    } finally {
      setCopying(false);
    }
  };

  const handleUomModeToggle = (mode) => {
    setFormData((prev) => {
      if (prev.input_uom_mode === mode) return prev;
      const converted = deriveCoatingCalculations(prev, lastEditedUnit);
      return {
        ...prev,
        input_uom_mode: mode,
        cost_per_gal_cdn: converted.costPerGal,
        cost_per_litre: converted.costPerLitre,
        thickness_mils: converted.wftMils,
        wft_microns: converted.wftMicrons,
        dft_mils: converted.dftMils,
        dft_microns: converted.dftMicrons,
        coverage_ft2_per_gal: converted.theoreticalFt2PerGal,
        coverage_25um_m2_per_l: converted.theoreticalM2PerL,
      };
    });
  };

  const openMappingModal = (mapping = null) => {
    if (!mapping) {
      setEditingMapping(null);
      setMappingFormData({
        repair_process_type_id: "",
        material_type: "",
        include_washing_cost: false,
        include_surface_prep_cost: false,
        counts_as_paint_layer: false,
        is_active: true,
        notes: "",
      });
      setShowMappingModal(true);
      return;
    }

    setEditingMapping(mapping);
    setMappingFormData({
      repair_process_type_id: String(
        mapping.repair_process_type_id || mapping.repairProcessTypeId || "",
      ),
      material_type: mapping.material_type || mapping.materialType || "",
      include_washing_cost: Boolean(
        mapping.include_washing_cost ?? mapping.includeWashingCost,
      ),
      include_surface_prep_cost: Boolean(
        mapping.include_surface_prep_cost ?? mapping.includeSurfacePrepCost,
      ),
      counts_as_paint_layer: Boolean(
        mapping.counts_as_paint_layer ?? mapping.countsAsPaintLayer,
      ),
      is_active: Boolean(mapping.is_active ?? mapping.isActive),
      notes: mapping.notes || "",
    });
    setShowMappingModal(true);
  };

  const handleSubmitMapping = async (e) => {
    e.preventDefault();
    setError(null);

    if (!String(mappingFormData.repair_process_type_id || "").trim()) {
      setError("Repair process type is required");
      return;
    }

    try {
      const payload = {
        repair_process_type_id: Number(mappingFormData.repair_process_type_id),
        material_type:
          String(mappingFormData.material_type || "").trim() || null,
        include_washing_cost: Boolean(mappingFormData.include_washing_cost),
        include_surface_prep_cost: Boolean(
          mappingFormData.include_surface_prep_cost,
        ),
        counts_as_paint_layer: Boolean(mappingFormData.counts_as_paint_layer),
        is_active: Boolean(mappingFormData.is_active),
        notes: String(mappingFormData.notes || "").trim() || null,
      };

      if (editingMapping?.id) {
        await api.put(
          `/repair-process-cost-mappings/${editingMapping.id}`,
          payload,
        );
      } else {
        await api.post("/repair-process-cost-mappings", payload);
      }

      setShowMappingModal(false);
      setEditingMapping(null);
      await fetchProcessCostMappings();
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to save process cost mapping",
      );
    }
  };

  const handleDeleteMapping = async (id) => {
    if (!window.confirm("Are you sure you want to delete this mapping?"))
      return;
    try {
      await api.delete(`/repair-process-cost-mappings/${id}`);
      await fetchProcessCostMappings();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete mapping");
    }
  };

  const handleToggleMappingActive = async (mapping) => {
    try {
      const id = mapping.id;
      const current = Boolean(mapping.is_active ?? mapping.isActive);
      await api.put(`/repair-process-cost-mappings/${id}`, {
        is_active: !current,
      });
      await fetchProcessCostMappings();
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to update mapping status",
      );
    }
  };

  const handleToggleMappingField = async (mapping, fieldName, errorMessage) => {
    const id = mapping?.id;
    if (!id || !fieldName) return;

    const toggleKey = `${id}:${fieldName}`;
    const currentValue = Boolean(mapping[fieldName]);

    setPendingMappingToggleKeys((prev) => {
      const next = new Set(prev);
      next.add(toggleKey);
      return next;
    });

    try {
      await api.put(`/repair-process-cost-mappings/${id}`, {
        [fieldName]: !currentValue,
      });
      await fetchProcessCostMappings();
    } catch (err) {
      setError(err.response?.data?.message || errorMessage);
    } finally {
      setPendingMappingToggleKeys((prev) => {
        const next = new Set(prev);
        next.delete(toggleKey);
        return next;
      });
    }
  };

  const renderMappingToggleSwitch = ({
    checked,
    onClick,
    disabled = false,
    label,
  }) => (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? "bg-blue-600" : "bg-gray-300"
      } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );

  const getMaterialTypeColor = (type) => {
    const normalizedType = String(type || "")
      .trim()
      .toLowerCase();
    const configuredColor = materialTypeColorMap[normalizedType];

    if (configuredColor) {
      const textColor = getBadgeTextColorFromBase(configuredColor);
      return {
        className: "border border-opacity-40",
        style: {
          backgroundColor: `${configuredColor}1f`,
          borderColor: configuredColor,
          ...(textColor ? { color: textColor } : {}),
        },
      };
    }

    return {
      className: "bg-gray-100 text-gray-800",
      style: {},
    };
  };

  const renderCommonFormFields = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Input UOM Mode
          </label>
          <div className="inline-flex rounded-md shadow-sm border border-gray-300 overflow-hidden">
            <button
              type="button"
              onClick={() => handleUomModeToggle("imperial")}
              className={`px-4 py-2 text-sm font-medium ${
                formData.input_uom_mode === "imperial"
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              US Imperial
            </button>
            <button
              type="button"
              onClick={() => handleUomModeToggle("metric")}
              className={`px-4 py-2 text-sm font-medium border-l border-gray-300 ${
                formData.input_uom_mode === "metric"
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              Metric
            </button>
          </div>
        </div>
        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Year *
          </label>
          <input
            type="number"
            required
            value={formData.year}
            onChange={(e) => {
              const newYear = parseInt(e.target.value, 10) || currentYear;
              setFormData({ ...formData, year: newYear });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={String(currentYear)}
          />
        </div>
        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Currency *
          </label>
          <select
            required
            value={formData.currency}
            onChange={(e) =>
              setFormData({ ...formData, currency: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="USD">USD</option>
            <option value="CAD">CAD</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {isLayerModal && (
          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Layer Name *
            </label>
            <SearchableAddDropdown
              value={formData.material_type}
              options={mergedMaterialTypeOptions}
              onChange={(nextValue) => {
                setFormData({ ...formData, material_type: nextValue });
              }}
              onAddOption={handleAddMaterialTypeOption}
              placeholder="Select layer name"
            />
          </div>
        )}
        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Coat Type
          </label>
          <SearchableAddDropdown
            value={formData.coat_type}
            options={mergedCoatTypeOptions}
            onChange={(nextValue) =>
              setFormData({ ...formData, coat_type: nextValue })
            }
            onAddOption={handleAddCoatTypeOption}
            placeholder="Select coat type"
          />
        </div>
        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {isLayerModal ? "Paint *" : "Paint Name *"}
          </label>
          {isLayerModal ? (
            <select
              required
              value={formData.paint_id}
              onChange={(e) => handleLayerPaintSelection(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select paint</option>
              {paintNameOptions.map((paintOption) => (
                <option
                  key={String(paintOption.value)}
                  value={String(paintOption.value)}
                >
                  {paintOption.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              required
              value={formData.material_name}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  material_name: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Corothane I Galvapac"
            />
          )}
        </div>

        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cost Input ({formData.currency})
          </label>
          <input
            type="number"
            step="0.01"
            value={
              formData.input_uom_mode === "metric"
                ? formData.cost_per_litre
                : formData.cost_per_gal_cdn
            }
            onChange={(e) => {
              setLastEditedUnit((prev) => ({
                ...prev,
                cost:
                  formData.input_uom_mode === "metric" ? "metric" : "imperial",
              }));
              setFormData({
                ...formData,
                [formData.input_uom_mode === "metric"
                  ? "cost_per_litre"
                  : "cost_per_gal_cdn"]: parseFloat(e.target.value) || 0,
              });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0.00"
          />
        </div>

        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {formData.input_uom_mode === "metric"
              ? "Coverage at 25um DFT (m2/litre)"
              : "Coverage at 1 mil DFT (ft2/US gallon)"}
          </label>
          <input
            type="number"
            step="0.01"
            value={
              formData.input_uom_mode === "metric"
                ? formData.coverage_25um_m2_per_l
                : formData.coverage_ft2_per_gal
            }
            onChange={(e) => {
              setLastEditedUnit((prev) => ({
                ...prev,
                coverage:
                  formData.input_uom_mode === "metric" ? "metric" : "imperial",
              }));
              setFormData({
                ...formData,
                [formData.input_uom_mode === "metric"
                  ? "coverage_25um_m2_per_l"
                  : "coverage_ft2_per_gal"]: parseFloat(e.target.value) || 0,
              });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0.00"
          />
        </div>

        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {formData.input_uom_mode === "metric"
              ? "Thickness at WFT (um)"
              : "Thickness at WFT (mils)"}
          </label>
          <input
            type="number"
            step="0.01"
            value={
              formData.input_uom_mode === "metric"
                ? formData.wft_microns
                : formData.thickness_mils
            }
            onChange={(e) => {
              setLastEditedUnit((prev) => ({
                ...prev,
                wft:
                  formData.input_uom_mode === "metric" ? "metric" : "imperial",
              }));
              setFormData({
                ...formData,
                [formData.input_uom_mode === "metric"
                  ? "wft_microns"
                  : "thickness_mils"]: parseFloat(e.target.value) || 0,
              });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0.00"
          />
        </div>

        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {formData.input_uom_mode === "metric"
              ? "Thickness at DFT (um)"
              : "Thickness at DFT (mils)"}
          </label>
          <input
            type="number"
            step="0.01"
            value={
              formData.input_uom_mode === "metric"
                ? formData.dft_microns
                : formData.dft_mils
            }
            onChange={(e) => {
              setLastEditedUnit((prev) => ({
                ...prev,
                dft:
                  formData.input_uom_mode === "metric" ? "metric" : "imperial",
              }));
              setFormData({
                ...formData,
                [formData.input_uom_mode === "metric"
                  ? "dft_microns"
                  : "dft_mils"]: parseFloat(e.target.value) || 0,
              });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0.00"
          />
        </div>

        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Coverage at DFT (
            {formData.input_uom_mode === "metric"
              ? "m2/litre"
              : "ft2/US gallon"}
            )
          </label>
          <input
            type="number"
            step="0.01"
            value={
              formData.input_uom_mode === "metric"
                ? derivedValues.appliedCoverageM2PerL
                : derivedValues.appliedCoverageFt2PerGal
            }
            readOnly
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
          />
        </div>

        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Converted Coverage (
            {formData.input_uom_mode === "metric"
              ? "ft2/US gallon"
              : "m2/litre"}
            )
          </label>
          <input
            type="number"
            step="0.01"
            value={
              formData.input_uom_mode === "metric"
                ? derivedValues.appliedCoverageFt2PerGal
                : derivedValues.appliedCoverageM2PerL
            }
            readOnly
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
          />
        </div>

        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Paint Cost per ft2 ({formData.currency})
          </label>
          <input
            type="number"
            step="0.0001"
            value={derivedValues.paintCostPerFt2}
            readOnly
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
          />
        </div>

        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Paint Cost per m2 ({formData.currency})
          </label>
          <input
            type="number"
            step="0.0001"
            value={derivedValues.paintCostPerM2}
            readOnly
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
          />
        </div>

        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sort Order *
          </label>
          <input
            type="number"
            step="1"
            min="0"
            required
            value={formData.sort_order}
            onChange={(e) =>
              setFormData({
                ...formData,
                sort_order:
                  e.target.value === "" ? "" : parseInt(e.target.value, 10),
              })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-2 md:col-span-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Active Status
          </label>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={
                formData.is_active !== undefined ? formData.is_active : true
              }
              onChange={(e) => {
                setFormData({ ...formData, is_active: e.target.checked });
              }}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900">
              This {isLayerModal ? "paint layer" : "paint"} is active
            </label>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start gap-2">
        <p className="text-gray-600">
          Manage paints and paint layers with detailed specifications and costs
        </p>
        <InlineFieldTooltip
          page="materials_master"
          section="page_description"
          fieldKey="materials_master_overview"
        />
      </div>

      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="Tabs">
          <button
            type="button"
            onClick={() => handleTabChange("paints")}
            className={`py-2 px-1 border-b-2 text-sm font-medium ${
              activeTab === "paints"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Paints
          </button>
          <button
            type="button"
            onClick={() => handleTabChange("paint_layers")}
            className={`py-2 px-1 border-b-2 text-sm font-medium ${
              activeTab === "paint_layers"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Paint Layers
          </button>
          <button
            type="button"
            onClick={() => handleTabChange("repair_rules")}
            className={`py-2 px-1 border-b-2 text-sm font-medium ${
              activeTab === "repair_rules"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Repair Rules
          </button>
          <button
            type="button"
            onClick={() => handleTabChange("process_cost_mappings")}
            className={`py-2 px-1 border-b-2 text-sm font-medium ${
              activeTab === "process_cost_mappings"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Process Cost Mappings
          </button>
        </nav>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {activeTab === "paints" ? (
        <>
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search paints..."
                value={paintSearchTerm}
                onChange={(e) => setPaintSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={paintYearFilter}
              onChange={(e) => setPaintYearFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Years</option>
              {paintAvailableYears.map((year) => (
                <option key={year} value={String(year)}>
                  {year}
                </option>
              ))}
            </select>
            <button
              onClick={() => openPaintModal()}
              className="inline-flex items-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
            >
              <FontAwesomeIcon
                icon={faPlus}
                className="-ml-0.5 mr-1.5 h-5 w-5"
              />
              Add Paint
            </button>
          </div>

          {loadingPaints ? (
            <div className="p-6 text-center text-gray-500">
              Loading paints...
            </div>
          ) : groupedPaints.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No paints found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12"></th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Year
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Currency
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paints Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sort
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Active
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {groupedPaints.map((yearData) => {
                    const isExpanded = expandedPaintYears.has(yearData.year);
                    return (
                      <React.Fragment key={yearData.year}>
                        <tr className="bg-gray-50 hover:bg-gray-100">
                          <td className="px-4 py-4">
                            <button
                              type="button"
                              onClick={() =>
                                togglePaintYearExpansion(yearData.year)
                              }
                              className="text-gray-500 hover:text-gray-700"
                              title={
                                isExpanded ? "Collapse year" : "Expand year"
                              }
                            >
                              <FontAwesomeIcon
                                icon={
                                  isExpanded ? faChevronDown : faChevronRight
                                }
                              />
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                            {yearData.year}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {yearData.currency || "CAD"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {(yearData.paints || []).length} paints
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                            -
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                            -
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  handleCopyPaintFromYear(yearData.year)
                                }
                                className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                              >
                                Copy
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleDeletePaintYear(yearData.year)
                                }
                                className="text-red-600 hover:text-red-900"
                                title={`Delete all paints for ${yearData.year}`}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </td>
                        </tr>

                        {isExpanded && (
                          <>
                            <tr>
                              <td className="px-4 py-2 bg-gray-50"></td>
                              <td
                                colSpan="6"
                                className="px-6 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider"
                              >
                                Paints for {yearData.year}
                              </td>
                            </tr>
                            {(yearData.paints || []).map((paint) => (
                              <tr key={paint.id} className="hover:bg-gray-50">
                                <td className="px-4 py-4"></td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  <div className="flex items-center">
                                    <FontAwesomeIcon
                                      icon={faCube}
                                      className="h-4 w-4 text-gray-400 mr-2"
                                    />
                                    {paint.paintName || paint.paint_name}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {paint.coatType || paint.coat_type || "-"}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  <div className="flex items-center">
                                    <FontAwesomeIcon
                                      icon={faDollarSign}
                                      className="h-4 w-4 text-green-600 mr-1"
                                    />
                                    {paint.costPerUnitCad ??
                                      paint.cost_per_unit_cad ??
                                      "-"}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                  {paint.costPerM2Cdn ??
                                    paint.cost_per_m2_cdn ??
                                    "-"}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                  {paint.sortOrder ?? paint.sort_order ?? 99}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={getMaterialIsActive(paint)}
                                    onClick={() =>
                                      handleTogglePaintActive(paint)
                                    }
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                      getMaterialIsActive(paint)
                                        ? "bg-blue-600"
                                        : "bg-gray-300"
                                    }`}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        getMaterialIsActive(paint)
                                          ? "translate-x-6"
                                          : "translate-x-1"
                                      }`}
                                    />
                                  </button>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <button
                                    onClick={() => openPaintModal(paint)}
                                    className="text-indigo-600 hover:text-indigo-900 mr-4"
                                  >
                                    <FontAwesomeIcon icon={faPencil} />
                                  </button>
                                  <button
                                    onClick={() => handleDeletePaint(paint.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : activeTab === "paint_layers" ? (
        <>
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search paint layers..."
                value={layerSearchTerm}
                onChange={(e) => setLayerSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={layerMaterialTypeFilter}
              onChange={(e) => setLayerMaterialTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              {mergedMaterialTypeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <select
              value={layerYearFilter}
              onChange={(e) => setLayerYearFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Years</option>
              {layerAvailableYears.map((year) => (
                <option key={year} value={String(year)}>
                  {year}
                </option>
              ))}
            </select>
            <button
              onClick={() => openLayerModal()}
              className="inline-flex items-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
            >
              <FontAwesomeIcon
                icon={faPlus}
                className="-ml-0.5 mr-1.5 h-5 w-5"
              />
              Add Paint Layer
            </button>
          </div>

          {loadingLayers ? (
            <div className="p-6 text-center text-gray-500">
              Loading paint layers...
            </div>
          ) : paintLayers.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No paint layers found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12"></th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Year
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Currency
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Layers Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sort
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Active
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paintLayers.map((yearData) => {
                    const isExpanded = expandedLayerYears.has(yearData.year);
                    return (
                      <React.Fragment key={yearData.year}>
                        <tr className="bg-gray-50 hover:bg-gray-100">
                          <td className="px-4 py-4">
                            <button
                              type="button"
                              onClick={() =>
                                toggleLayerYearExpansion(yearData.year)
                              }
                              className="text-gray-500 hover:text-gray-700"
                              title={
                                isExpanded ? "Collapse year" : "Expand year"
                              }
                            >
                              <FontAwesomeIcon
                                icon={
                                  isExpanded ? faChevronDown : faChevronRight
                                }
                              />
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                            {yearData.year}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {yearData.currency || "CAD"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {(yearData.materials || []).length} layers
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                            -
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                            -
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  handleImportPreviousYear(yearData.year)
                                }
                                className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border border-green-300 text-green-700 bg-green-50 hover:bg-green-100"
                              >
                                Import
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleCopyFromYear(yearData.year)
                                }
                                className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                              >
                                Copy
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleDeleteLayerYear(yearData.year)
                                }
                                className="text-red-600 hover:text-red-900"
                                title={`Delete all paint layers for ${yearData.year}`}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </td>
                        </tr>

                        {isExpanded && (
                          <>
                            <tr>
                              <td className="px-4 py-2 bg-gray-50"></td>
                              <td
                                colSpan="6"
                                className="px-6 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider"
                              >
                                Paint Layers for {yearData.year}
                              </td>
                            </tr>
                            {sortBySortOrderAndName(
                              yearData.materials || [],
                            ).map((material) => (
                              <tr
                                key={material.id}
                                className="hover:bg-gray-50"
                              >
                                <td className="px-4 py-4"></td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getMaterialTypeColor(material.materialType || material.material_type).className}`}
                                    style={
                                      getMaterialTypeColor(
                                        material.materialType ||
                                          material.material_type,
                                      ).style
                                    }
                                  >
                                    {String(
                                      material.materialType ||
                                        material.material_type ||
                                        "",
                                    ).toUpperCase()}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  <div className="flex items-center">
                                    <FontAwesomeIcon
                                      icon={faCube}
                                      className="h-4 w-4 text-gray-400 mr-2"
                                    />
                                    {material.materialName ||
                                      material.material_name}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    Cost/m2:{" "}
                                    {material.costPerM2Cdn ??
                                      material.cost_per_m2_cdn ??
                                      "-"}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                  <div className="flex items-center">
                                    <FontAwesomeIcon
                                      icon={faDollarSign}
                                      className="h-4 w-4 text-green-600 mr-1"
                                    />
                                    <span className="font-medium">
                                      {material.costPerUnitCad ??
                                        material.cost_per_unit_cad ??
                                        "-"}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                  {material.sortOrder ??
                                    material.sort_order ??
                                    99}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={getMaterialIsActive(material)}
                                    onClick={() =>
                                      handleToggleLayerActive(material)
                                    }
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                      getMaterialIsActive(material)
                                        ? "bg-blue-600"
                                        : "bg-gray-300"
                                    }`}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        getMaterialIsActive(material)
                                          ? "translate-x-6"
                                          : "translate-x-1"
                                      }`}
                                    />
                                  </button>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={() => openLayerModal(material)}
                                      className="text-blue-600 hover:text-blue-900"
                                      title="Edit"
                                    >
                                      <FontAwesomeIcon
                                        icon={faPencil}
                                        className="h-4 w-4"
                                      />
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleDeleteLayer(material.id)
                                      }
                                      className="text-red-600 hover:text-red-900"
                                      title="Delete"
                                    >
                                      <FontAwesomeIcon
                                        icon={faTrash}
                                        className="h-4 w-4"
                                      />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : activeTab === "repair_rules" ? (
        <RepairRulesManagement />
      ) : (
        <>
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search process mappings..."
                value={mappingSearchTerm}
                onChange={(e) => setMappingSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={() => openMappingModal()}
              className="inline-flex items-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
            >
              <FontAwesomeIcon
                icon={faPlus}
                className="-ml-0.5 mr-1.5 h-5 w-5"
              />
              Add Mapping
            </button>
          </div>

          {loadingProcessMappings ? (
            <div className="p-6 text-center text-gray-500">
              Loading process cost mappings...
            </div>
          ) : processCostMappings.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No process cost mappings found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Repair Process
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Material Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Include Washing Cost
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Include Surface Prep Cost
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Counts as Paint Layer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Active
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedProcessCostMappings.map((mapping) => {
                    const includeWashing = Boolean(
                      mapping.include_washing_cost ??
                      mapping.includeWashingCost,
                    );
                    const includeSurfacePrep = Boolean(
                      mapping.include_surface_prep_cost ??
                      mapping.includeSurfacePrepCost,
                    );
                    const isLayer = Boolean(
                      mapping.counts_as_paint_layer ??
                      mapping.countsAsPaintLayer,
                    );
                    const isActive = Boolean(
                      mapping.is_active ?? mapping.isActive,
                    );
                    const pendingWashingToggle = pendingMappingToggleKeys.has(
                      `${mapping.id}:include_washing_cost`,
                    );
                    const pendingSurfacePrepToggle =
                      pendingMappingToggleKeys.has(
                        `${mapping.id}:include_surface_prep_cost`,
                      );
                    const pendingPaintLayerToggle =
                      pendingMappingToggleKeys.has(
                        `${mapping.id}:counts_as_paint_layer`,
                      );

                    return (
                      <tr key={mapping.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {mapping.process_name || mapping.processName || "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {mapping.material_type || mapping.materialType || "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {renderMappingToggleSwitch({
                            checked: includeWashing,
                            disabled: pendingWashingToggle,
                            label: `Toggle washing cost for ${mapping.process_name || mapping.processName || "process mapping"}`,
                            onClick: () =>
                              handleToggleMappingField(
                                mapping,
                                "include_washing_cost",
                                "Failed to update washing cost setting",
                              ),
                          })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {renderMappingToggleSwitch({
                            checked: includeSurfacePrep,
                            disabled: pendingSurfacePrepToggle,
                            label: `Toggle surface prep cost for ${mapping.process_name || mapping.processName || "process mapping"}`,
                            onClick: () =>
                              handleToggleMappingField(
                                mapping,
                                "include_surface_prep_cost",
                                "Failed to update surface prep cost setting",
                              ),
                          })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {renderMappingToggleSwitch({
                            checked: isLayer,
                            disabled: pendingPaintLayerToggle,
                            label: `Toggle paint layer flag for ${mapping.process_name || mapping.processName || "process mapping"}`,
                            onClick: () =>
                              handleToggleMappingField(
                                mapping,
                                "counts_as_paint_layer",
                                "Failed to update paint layer setting",
                              ),
                          })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {renderMappingToggleSwitch({
                            checked: isActive,
                            label: `Toggle active status for ${mapping.process_name || mapping.processName || "process mapping"}`,
                            onClick: () => handleToggleMappingActive(mapping),
                          })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => openMappingModal(mapping)}
                            className="text-blue-600 hover:text-blue-900 mr-4"
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faPencil} />
                          </button>
                          <button
                            onClick={() => handleDeleteMapping(mapping.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto w-full z-50 p-4 pt-20">
          <div className="mx-auto p-4 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-4">
                <span>
                  {editingRecord
                    ? `Edit ${isLayerModal ? "Paint Layer" : "Paint"}`
                    : `Add New ${isLayerModal ? "Paint Layer" : "Paint"}`}
                </span>
                <InlineFieldTooltip
                  page="materials_master"
                  section="modal_titles"
                  fieldKey={
                    isLayerModal ? "material_master_form" : "paint_master_form"
                  }
                  fallbackText="Create or edit paint records."
                  title={isLayerModal ? "Paint Layer Form" : "Paint Form"}
                />
              </h3>

              <form onSubmit={handleSubmit}>
                {renderCommonFormFields()}

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm(modalEntity);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    {editingRecord ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showMappingModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto w-full z-50 p-4 pt-20">
          <div className="mx-auto p-4 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingMapping
                  ? "Edit Process Cost Mapping"
                  : "Add Process Cost Mapping"}
              </h3>

              <form onSubmit={handleSubmitMapping}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="mb-2 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Repair Process Type *
                    </label>
                    <select
                      required
                      value={mappingFormData.repair_process_type_id}
                      onChange={(e) =>
                        setMappingFormData((prev) => ({
                          ...prev,
                          repair_process_type_id: e.target.value,
                        }))
                      }
                      disabled={Boolean(editingMapping)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    >
                      <option value="">Select process type</option>
                      {mappingProcessTypes.map((type) => (
                        <option key={type.id} value={String(type.id)}>
                          {type.processName || type.process_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Material Type Key
                    </label>
                    <input
                      type="text"
                      value={mappingFormData.material_type}
                      onChange={(e) =>
                        setMappingFormData((prev) => ({
                          ...prev,
                          material_type: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. prime, stripecoat"
                    />
                  </div>

                  <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={mappingFormData.include_washing_cost}
                      onChange={(e) =>
                        setMappingFormData((prev) => ({
                          ...prev,
                          include_washing_cost: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    Include washing cost
                  </label>

                  <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={mappingFormData.include_surface_prep_cost}
                      onChange={(e) =>
                        setMappingFormData((prev) => ({
                          ...prev,
                          include_surface_prep_cost: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    Include surface prep cost
                  </label>

                  <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={mappingFormData.counts_as_paint_layer}
                      onChange={(e) =>
                        setMappingFormData((prev) => ({
                          ...prev,
                          counts_as_paint_layer: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    Counts as paint layer
                  </label>

                  <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={mappingFormData.is_active}
                      onChange={(e) =>
                        setMappingFormData((prev) => ({
                          ...prev,
                          is_active: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    Active
                  </label>

                  <div className="mb-2 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={mappingFormData.notes}
                      onChange={(e) =>
                        setMappingFormData((prev) => ({
                          ...prev,
                          notes: e.target.value,
                        }))
                      }
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowMappingModal(false);
                      setEditingMapping(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    {editingMapping ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showCopyModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Copy To Year
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Copy all {copyEntity === "paint" ? "paints" : "paint layers"} from{" "}
              <strong>{copySourceYear}</strong> to a new year.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Year
            </label>
            <input
              type="number"
              value={copyTargetYear}
              onChange={(e) => {
                setCopyTargetYear(e.target.value);
                setCopyError("");
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. 2027"
            />
            {copyError && (
              <p className="mt-2 text-sm text-red-600">{copyError}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (copying) return;
                  setShowCopyModal(false);
                  setCopyEntity("layer");
                  setCopySourceYear(null);
                  setCopyTargetYear("");
                  setCopyError("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={copying}
                onClick={handleConfirmCopy}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {copying ? "Copying..." : "Copy"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialsMasterManagement;
